import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import GlassCard from '@/effects/ui/GlassCard';
import { Receipt, Plus, Trash2, Calculator, Wallet, Utensils, Plane, Hotel, ShoppingBag, Ticket, Coffee, Gift } from 'lucide-react';

const categoryMeta = {
  food: { label: '餐饮', icon: Utensils, color: '#fbbf24' },
  transport: { label: '交通', icon: Plane, color: '#7eccff' },
  hotel: { label: '住宿', icon: Hotel, color: '#a78bfa' },
  ticket: { label: '门票', icon: Ticket, color: '#34d399' },
  shopping: { label: '购物', icon: ShoppingBag, color: '#f472b6' },
  coffee: { label: '饮品', icon: Coffee, color: '#fb923c' },
  gift: { label: '伴手礼', icon: Gift, color: '#60a5fa' },
  other: { label: '其他', icon: Wallet, color: '#94a3b8' },
};

const defaultExpenses = [
  { id: 1, category: 'transport', amount: 1280, desc: '往返机票', date: '2026-08-01' },
  { id: 2, category: 'hotel', amount: 2680, desc: '4晚海景酒店', date: '2026-08-01' },
  { id: 3, category: 'food', amount: 320, desc: '海鲜晚餐', date: '2026-08-01' },
  { id: 4, category: 'ticket', amount: 580, desc: '景区门票', date: '2026-08-02' },
];

export default function Expense() {
  const [expenses, setExpenses] = useState(defaultExpenses);
  const [showCalc, setShowCalc] = useState(false);
  const [calcDisplay, setCalcDisplay] = useState('0');
  const [calcPrev, setCalcPrev] = useState(null);
  const [calcOp, setCalcOp] = useState(null);
  const [newExpense, setNewExpense] = useState({ category: 'food', amount: '', desc: '', date: new Date().toISOString().slice(0, 10) });
  const [editingId, setEditingId] = useState(null);

  // 计算器逻辑
  const calcInput = (val) => {
    if (val === 'C') { setCalcDisplay('0'); setCalcPrev(null); setCalcOp(null); return; }
    if (val === '±') { setCalcDisplay(String(-Number(calcDisplay))); return; }
    if (val === '%') { setCalcDisplay(String(Number(calcDisplay) / 100)); return; }
    if (['+', '-', '×', '÷'].includes(val)) {
      setCalcPrev(Number(calcDisplay));
      setCalcOp(val);
      setCalcDisplay('0');
      return;
    }
    if (val === '=') {
      if (calcPrev === null || !calcOp) return;
      const curr = Number(calcDisplay);
      const result = calcOp === '+' ? calcPrev + curr
        : calcOp === '-' ? calcPrev - curr
        : calcOp === '×' ? calcPrev * curr
        : curr === 0 ? 0 : calcPrev / curr;
      setCalcDisplay(String(Number(Number(result).toFixed(8))));
      setCalcPrev(null);
      setCalcOp(null);
      return;
    }
    if (val === '.') {
      if (!calcDisplay.includes('.')) setCalcDisplay(calcDisplay + '.');
      return;
    }
    setCalcDisplay(calcDisplay === '0' ? val : calcDisplay + val);
  };

  const useCalcResult = () => {
    const val = Number(calcDisplay);
    if (val > 0) {
      setNewExpense(prev => ({ ...prev, amount: calcDisplay }));
      setShowCalc(false);
    }
  };

  const addExpense = () => {
    if (!newExpense.amount || Number(newExpense.amount) <= 0) return;
    if (editingId !== null) {
      setExpenses(prev => prev.map(e => e.id === editingId ? { ...e, ...newExpense, amount: Number(newExpense.amount) } : e));
      setEditingId(null);
    } else {
      setExpenses(prev => [...prev, { id: Date.now(), ...newExpense, amount: Number(newExpense.amount) }]);
    }
    setNewExpense({ category: 'food', amount: '', desc: '', date: new Date().toISOString().slice(0, 10) });
  };

  const startEdit = (exp) => {
    setEditingId(exp.id);
    setNewExpense({ category: exp.category, amount: String(exp.amount), desc: exp.desc, date: exp.date });
  };

  const removeExpense = (id) => setExpenses(prev => prev.filter(e => e.id !== id));

  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const byCategory = Object.entries(categoryMeta).map(([key, meta]) => ({
    key, ...meta, total: expenses.filter(e => e.category === key).reduce((s, e) => s + e.amount, 0),
    count: expenses.filter(e => e.category === key).length,
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  return (
    <>
      <Navbar />
      <div className="relative pt-24 pb-16 px-4 max-w-6xl mx-auto">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold cb mb-2 flex items-center gap-3">
              <Receipt className="w-7 h-7" style={{ color: '#7eccff' }} />
              旅行记账
            </h1>
            <p className="cd text-base">实时记录开销，分类统计，掌控旅行预算</p>
          </div>
          <button onClick={() => setShowCalc(!showCalc)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm"
            style={{ background: 'linear-gradient(135deg, var(--glow), var(--mid))' }}>
            <Calculator className="w-4 h-4" /> 计算器
          </button>
        </div>

        {/* 总支出 */}
        <GlassCard className="p-5 mb-6" delay={0}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm cd mb-1">总支出</div>
              <div className="text-4xl font-bold" style={{ color: '#eef3f7' }}>¥ {total.toLocaleString()}</div>
            </div>
            <div className="text-right">
              <div className="text-sm cd mb-1">共 {expenses.length} 笔</div>
              <div className="text-sm cd">日均 ¥ {expenses.length > 0 ? Math.ceil(total / Math.max(1, new Set(expenses.map(e => e.date)).size)).toLocaleString() : 0}</div>
            </div>
          </div>
        </GlassCard>

        {/* 分类统计 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {byCategory.map(cat => {
            const Icon = cat.icon;
            const pct = Math.round(cat.total / total * 100) || 0;
            return (
              <GlassCard key={cat.key} className="p-3" delay={0.05}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" style={{ color: cat.color }} />
                  <span className="text-xs cd">{cat.label}</span>
                </div>
                <div className="text-sm font-medium cb">¥ {cat.total.toLocaleString()}</div>
                <div className="flex justify-between mt-1">
                  <div className="h-1.5 flex-1 rounded-full overflow-hidden mr-2" style={{ background: 'rgba(106,179,214,0.1)' }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: cat.color }} />
                  </div>
                  <span className="text-[10px] cd">{pct}%</span>
                </div>
              </GlassCard>
            );
          })}
        </div>

        {/* 新增支出 */}
        <GlassCard className="p-5 mb-6" delay={0.1}>
          <div className="text-sm font-medium cb mb-3">{editingId ? '编辑支出' : '添加支出'}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <select value={newExpense.category} onChange={e => setNewExpense(p => ({ ...p, category: e.target.value }))}
              className="text-sm rounded-lg px-3 py-2.5 cb outline-none col-span-2 md:col-span-1"
              style={{ background: 'rgba(106,179,214,0.08)', border: '1px solid rgba(156,200,224,0.1)' }}>
              {Object.entries(categoryMeta).map(([k, v]) => <option key={k} value={k}>{v.icon && ''} {v.label}</option>)}
            </select>
            <input type="number" value={newExpense.amount} onChange={e => setNewExpense(p => ({ ...p, amount: e.target.value }))}
              placeholder="金额" inputMode="decimal"
              className="text-sm rounded-lg px-3 py-2.5 cb outline-none"
              style={{ background: 'rgba(106,179,214,0.05)', border: '1px solid rgba(156,200,224,0.1)' }} />
            <input type="text" value={newExpense.desc} onChange={e => setNewExpense(p => ({ ...p, desc: e.target.value }))}
              placeholder="备注"
              className="text-sm rounded-lg px-3 py-2.5 cb outline-none col-span-2"
              style={{ background: 'rgba(106,179,214,0.05)', border: '1px solid rgba(156,200,224,0.1)' }} />
            <button onClick={addExpense}
              className="flex items-center justify-center gap-1.5 rounded-lg text-sm text-white py-2.5"
              style={{ background: 'linear-gradient(135deg, var(--glow), var(--mid))' }}>
              <Plus className="w-4 h-4" /> {editingId ? '保存' : '添加'}
            </button>
          </div>
        </GlassCard>

        {/* 计算器弹窗 */}
        {showCalc && (
          <GlassCard className="p-5 mb-6 max-w-xs mx-auto" delay={0}>
            <div className="text-sm font-medium cb mb-3 flex items-center gap-2"><Calculator className="w-4 h-4" /> 计算器</div>
            <div className="rounded-lg p-3 mb-3 text-right text-2xl font-mono cb overflow-hidden"
              style={{ background: 'rgba(106,179,214,0.05)', border: '1px solid rgba(156,200,224,0.1)' }}>
              <div className="text-xs cd h-4">{calcOp ? `${calcPrev} ${calcOp}` : ''}</div>
              <div className="truncate">{calcDisplay}</div>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {['C', '±', '%', '÷', '7', '8', '9', '×', '4', '5', '6', '-', '1', '2', '3', '+', '0', '.', '=', '='].map((btn, i) => (
                <button key={i} onClick={() => calcInput(btn)}
                  className={`py-3 rounded-lg text-sm font-medium transition
                    ${['C', '±', '%'].includes(btn) ? 'cd hover:!cb' : ''}
                    ${['÷', '×', '-', '+'].includes(btn) ? 'text-white' : ''}
                    ${btn === '=' ? 'text-white' : ''}
                    ${btn === '0' ? 'col-span-1' : ''}`}
                  style={{
                    background: ['÷','×','-','+'].includes(btn) ? 'rgba(106,179,214,0.2)' :
                      btn === '=' ? 'linear-gradient(135deg, var(--glow), var(--mid))' :
                      ['C','±','%'].includes(btn) ? 'rgba(106,179,214,0.08)' : 'rgba(106,179,214,0.04)',
                  }}>{btn}</button>
              ))}
            </div>
            <button onClick={useCalcResult} className="w-full mt-3 py-2 rounded-lg text-sm text-white"
              style={{ background: 'linear-gradient(135deg, var(--glow), var(--mid))' }}>
              填入金额
            </button>
          </GlassCard>
        )}

        {/* 支出列表 */}
        <div className="space-y-2">
          {expenses.sort((a, b) => b.date.localeCompare(a.date) || b.id - a.id).map((exp, idx) => {
            const meta = categoryMeta[exp.category];
            const Icon = meta?.icon || Wallet;
            return (
              <GlassCard key={exp.id} className="p-3 flex items-center gap-3" delay={0.12 + idx * 0.03}>
                <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: (meta?.color || '#94a3b8') + '22' }}>
                  <Icon className="w-4.5 h-4.5" style={{ color: meta?.color || '#94a3b8' }} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm cb truncate">{exp.desc || meta?.label || '其他'}</div>
                  <div className="text-xs cd">{exp.date}</div>
                </div>
                <div className="text-sm font-medium" style={{ color: '#eef3f7' }}>-¥{exp.amount.toLocaleString()}</div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(exp)} className="text-[10px] cd hover:!cb px-1">编辑</button>
                  <button onClick={() => removeExpense(exp.id)} className="text-[10px] text-red-400 hover:!text-red-300 p-1"><Trash2 className="w-3 h-3" /></button>
                </div>
              </GlassCard>
            );
          })}
        </div>
      </div>
    </>
  );
}
